import React, { Fragment, useMemo, useState } from 'react';
import AppTable from '../../../../constants/AppTable';
import { LoadingSpinner } from '../../../../constants/AppLoading';
import DialogBox from '../../../../constants/DialogBox';
import { Grid } from '@mui/material';
import InputTextBox from '../../../../constants/InputTextBox';
import { toleValidationSchema } from '../../../../helpers/validation';
import { useFormik } from 'formik';
import { EndBaseLine } from '../../../../constants/AppSpace';
import { Button } from '../../../../constants/Button';
import { putDataToTheServer } from '../../../../helpers/api';
import AppURL from '../../../../constants/AppURL';
import { ToastSuccess, ToastWarning } from '../../../../constants/Toast';
import DefaultSelectInputBox from '../../../../constants/DefaultSelectBox';
import { YesNoOptions } from '../../../../constants/Options';
import NepaliInputBox from '../../../../constants/NepaliInputBox';
import { handleNepaliInputChange } from '../../../../helpers/handleChange';

interface Designation {
    sn: "",
    name: "",
    name_nep: "",
    remarks: "",
    status: "",
}

interface MargaToleListViewProps {
    results: {
        results: Designation[];
        previous: string | null;
        next: string | null;
    } | null;
    loading: boolean;
    error: string | null;
    setOnURL: React.Dispatch<React.SetStateAction<string>>;
    handleFormSuccess: () => void;
}

const DesignationListView: React.FC<MargaToleListViewProps> = ({ results, loading, error, handleFormSuccess, setOnURL }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState<Designation | null>(null);

    const columns = [
        { header: 'SN', accessor: 'sn', sortable: false },
        { header: 'Designation Name', accessor: 'name', sortable: false },
        { header: 'Designation Name(Nepali)', accessor: 'name_nep', sortable: true },
        { header: 'Remarks', accessor: 'remarks', sortable: false },
        { header: 'Active', accessor: 'status', sortable: true },
        { header: 'Actions', accessor: 'actions', action: true },
    ];

    const modifiedData = useMemo(() => {
        if (!results) return [];
        return results.results.map((item: Designation) => ({
            sn: item.sn,
            name: item.name,
            name_nep: item.name_nep,
            remarks: item.remarks,
            status: item.status, // Keep active as a number
        }));
    }, [results]);

    const handleEdit = (item: Designation) => {
        setCurrentItem(item);
        setDialogOpen(true);
    };

    const formik = useFormik({
        initialValues: {
            name: currentItem?.name || "",
            name_nep: currentItem?.name_nep || "",
            remarks: currentItem?.remarks || "",
            status: currentItem?.status?.toString() === "1" ? "1" : "2", // Initialize as a string for form handling
        },
        // Uncomment and use validationSchema as needed
        // validationSchema: toleValidationSchema,
        enableReinitialize: true,
        onSubmit: async (values, action) => {
            const formData = new FormData();
            formData.append("name", values.name);
            formData.append("name_nep", values.name_nep);
            formData.append("remarks", values.remarks);
            formData.append("status", values.status === "1" ? "1" : "0"); // Convert string to number for API

            try {
                await putDataToTheServer(AppURL.PayrollURL + 'payroll/designation/retrieve/update/view/' + currentItem?.sn, formData).then((res) => {
                    if (res?.status === 200) {
                        handleFormSuccess();
                        setDialogOpen(false);
                        ToastSuccess({ message: res?.message });
                    } else if (res?.status === 400) {
                        ToastWarning({ message: res?.message });
                    } else {
                        ToastWarning({ message: "Some Issue" });
                    }
                });
            } catch (error) {
                console.log(error);
            }
        },
    });

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <LoadingSpinner />
            </div>
        );
    }

    if (error) {
        return <p>Error loading data: {error}</p>;
    }

    return (
        <Fragment>
            <AppTable
                columns={columns}
                data={modifiedData}
                onEdit={handleEdit}
                onNext={() => results?.next && setOnURL(results.next)}
                onPrev={() => results?.previous && setOnURL(results.previous)}
            />
            {dialogOpen && currentItem && (
                <DialogBox open={dialogOpen} headerName='Designation Edit Update' close={() => setDialogOpen(false)}>
                    <form onSubmit={formik.handleSubmit}>
                        <Grid container columnSpacing={3} rowSpacing={0.8}>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='name'
                                    label='Designation Name'
                                    handleChange={formik.handleChange}
                                    value={formik.values.name}
                                    error={formik.errors.name}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.name}
                                    required={true}
                                    placeholder='Enter Designation Name'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <NepaliInputBox
                                    label='Designation Name(Nepali)'
                                    name='name_nep'
                                    value={formik.values.name_nep}
                                    handleChange={(e) => handleNepaliInputChange(e, { setFieldValue: formik.setFieldValue, name: 'name_nep' })}
                                    required={true}
                                    handleBlur={formik.handleBlur}
                                    error={typeof formik.errors.name_nep === 'string' ? formik.errors.name_nep : undefined}
                                    placeholder='Enter Designation name(Nepali)'
                                    touched={formik.touched.name_nep}
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='remarks'
                                    label='Remarks'
                                    handleChange={formik.handleChange}
                                    value={formik.values.remarks}
                                    error={formik.errors.remarks}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.remarks}
                                    required={false}
                                    placeholder='Remarks'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <DefaultSelectInputBox
                                    name="status"
                                    label='Active'
                                    disabled={false} // Adjust based on your needs
                                    options={YesNoOptions}
                                    value={formik.values.status}
                                    optionSelect={(name, selectedOption) => {
                                        formik.setFieldValue(name, selectedOption.value);
                                    }}
                                    clear={true}
                                    placeholder="Select Status Options"
                                />
                            </Grid>

                            <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                                <EndBaseLine top={18}>
                                    <Button btn_type='update' type='submit' el='button' />
                                </EndBaseLine>
                            </Grid>
                        </Grid>
                    </form>
                </DialogBox>
            )}
        </Fragment>
    );
};

export default DesignationListView;

